import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { SessionRestore } from 'components/shared/auth/SessionRestore/SessionRestore';
import { NotifierContainer } from 'components/shared/messages/Notifier/NotifierContainer';
import { LocaleProvider } from 'context/locale';
import { getFetchClient } from 'fetch/client';
import React, { useEffect } from 'react';
import { ClientContextProvider } from 'react-fetching-library';
import { Provider } from 'react-redux';
import { ThroughProvider } from 'react-through';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { updateWindowInfo } from 'store/general';
import { getConfiguredStore } from 'store/store';
import { osinTheme } from 'styles/themes/osinTheme';
import { getAppConfig } from 'utils/config/appConfig';
import { setAppRouter } from 'utils/navigation/navigate';
import { MainView } from 'views/Main.view';
import { LicenseManager } from 'ag-grid-enterprise';
import './App.scss';
import { debounce } from 'lodash';
import { InterfaceBlocker } from 'components/shared/loaders/InterfaceBlocker/InterfaceBlocker';
import { EnvironmentProvider } from 'components/shared/environment/EnvironmentProvider/EnvironmentProvider';
import { useDispatch } from 'react-redux';
import { ModalClosedProvider } from 'context/ModalClosedContext';

const WindowResizeHandler: React.FC = () => {
  const dispatch = useDispatch();

  const setViewportResolution = debounce(() => {
    dispatch(updateWindowInfo(window.innerWidth, window.innerHeight));
  }, 500);

  useEffect(() => {
    if (window) {
      setViewportResolution();
      window.addEventListener('resize', setViewportResolution);
      return () => window.removeEventListener('resize', setViewportResolution);
    }
  }, [setViewportResolution]);

  return null;
};

const AppShell: React.FC = () => (
  <StyledEngineProvider injectFirst>
    <ThemeProvider theme={osinTheme}>
      <CssBaseline />
      <WindowResizeHandler />
      <EnvironmentProvider>
        <ModalClosedProvider>
          <SessionRestore>
            <NotifierContainer>
              <InterfaceBlocker />
              <MainView />
            </NotifierContainer>
          </SessionRestore>
        </ModalClosedProvider>
      </EnvironmentProvider>
    </ThemeProvider>
  </StyledEngineProvider>
);

const router = createBrowserRouter([{ path: '*', Component: AppShell }]);
setAppRouter(router);

const App: React.FC = () => {
  LicenseManager.setLicenseKey(getAppConfig('OSIN20_AG_GRID_LICENSE'));

  const fetchClient = getFetchClient(getConfiguredStore());

  if (import.meta.env.DEV) {
    import('./mocks/browser').then(({ worker }) => {
      worker.start();
    });
  }

  return (
    <LocaleProvider locale="pl">
      <ThroughProvider>
        <Provider store={getConfiguredStore()}>
          <ClientContextProvider client={fetchClient}>
            <RouterProvider router={router} />
          </ClientContextProvider>
        </Provider>
      </ThroughProvider>
    </LocaleProvider>
  );
};

export default App;
